import { Plugin } from './types';

/**
 * Utility function for users to register their plugins
 * Handles all the window registration logic automatically
 */
export function registerPlugin(plugin: Plugin): void {
  if (typeof window === 'undefined') {
    // SSR environment - just return
    return;
  }

  try {
    // Method 1: Use the plugin system's registerPlugin if available
    if (window.registerPlugin && typeof window.registerPlugin === 'function') {
      window.registerPlugin(plugin);
      return;
    }

    // Method 2: Add to window.plugins array (fallback)
    if (!window.plugins) {
      window.plugins = [];
    }
    
    // Check if plugin already exists (prevent duplicates)
    const existingIndex = window.plugins.findIndex(p => p.meta.name === plugin.meta.name);
    if (existingIndex !== -1) {
      // Replace existing plugin
      window.plugins[existingIndex] = plugin;
      console.log(`Plugin "${plugin.meta.name}" updated`);
    } else {
      // Add new plugin
      window.plugins.push(plugin);
      console.log(`Plugin "${plugin.meta.name}" registered`);
    }

    // Method 3: Also add to named registry (for direct access)
    if (!window.pluginRegistry) {
      window.pluginRegistry = {};
    }
    window.pluginRegistry[plugin.meta.name] = plugin;

    // Dispatch custom event for plugin registration
    const event = new CustomEvent('plugin-registered', {
      detail: { plugin }
    });
    window.dispatchEvent(event);

  } catch (error) {
    console.error(`Failed to register plugin "${plugin.meta.name}":`, error);
  }
}

/**
 * Utility function to unregister a plugin
 */
export function unregisterPlugin(pluginName: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Remove from window.plugins array
    if (window.plugins) {
      window.plugins = window.plugins.filter(p => p.meta.name !== pluginName);
    }

    // Remove from window.pluginRegistry
    if (window.pluginRegistry && window.pluginRegistry[pluginName]) {
      delete window.pluginRegistry[pluginName];
    }

    // Dispatch unregister event
    const event = new CustomEvent('plugin-unregistered', {
      detail: { pluginName }
    });
    window.dispatchEvent(event);

    console.log(`Plugin "${pluginName}" unregistered`);
  } catch (error) {
    console.error(`Failed to unregister plugin "${pluginName}":`, error);
  }
}

/**
 * Get all registered plugins
 */
export function getRegisteredPlugins(): Plugin[] {
  if (typeof window === 'undefined') {
    return [];
  }
  return window.plugins || [];
}

/**
 * Get a specific plugin by name
 */
export function getPlugin(name: string): Plugin | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return window.pluginRegistry?.[name];
}

/**
 * Check if a plugin is registered
 */
export function isPluginRegistered(name: string): boolean {
  return getPlugin(name) !== undefined;
}

/**
 * Advanced registration with options
 */
export interface PluginRegistrationOptions {
  replace?: boolean; // Replace if exists
  silent?: boolean;  // Don't log to console
  priority?: number; // Plugin priority
}

export function registerPluginWithOptions(
  plugin: Plugin, 
  options: PluginRegistrationOptions = {}
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const { replace = true, silent = false, priority = 0 } = options;

  try {
    // Add priority to plugin meta if provided
    const pluginWithPriority = {
      ...plugin,
      meta: {
        ...plugin.meta,
        priority
      }
    };

    // Check for existing plugin
    const existingIndex = window.plugins?.findIndex(p => p.meta.name === plugin.meta.name) ?? -1;
    
    if (existingIndex !== -1 && !replace) {
      if (!silent) {
        console.warn(`Plugin "${plugin.meta.name}" already exists and replace=false`);
      }
      return;
    }

    // Use the simple register function
    registerPlugin(pluginWithPriority);

    // Sort by priority if priorities are set
    if (window.plugins && priority !== 0) {
      window.plugins.sort((a, b) => (a.meta.priority || 0) - (b.meta.priority || 0));
    }

    if (!silent) {
      console.log(`Plugin "${plugin.meta.name}" registered with priority ${priority}`);
    }

  } catch (error) {
    console.error(`Failed to register plugin "${plugin.meta.name}" with options:`, error);
  }
}
